import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import GroupLeaderBottomNav from '../../../components/GroupLeaderBottomNav';
import PullToRefresh from '../../../components/PullToRefresh';
import {
  ArrowLeft,
  UserCircle,
  Users,
  TrendUp,
  ClockCounterClockwise,
  UsersThree,
  Bell
} from '@phosphor-icons/react';

export default function GroupLeaderSubgroups() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [subgroups, setSubgroups] = useState([]);
  const [selectedSubgroup, setSelectedSubgroup] = useState(null);
  const [subgroupDetails, setSubgroupDetails] = useState(null);
  const [groupData, setGroupData] = useState(null);

  useEffect(() => {
    fetchSubgroups();
  }, []);

  const fetchSubgroups = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Get group leader's group
      const { data: groupMember } = await supabase
        .from('group_members')
        .select('group_id, groups(id, name, depth)')
        .eq('user_id', user.id)
        .single();

      // Get user's leadership level
      const { data: userData } = await supabase
        .from('users')
        .select('leadership_level')
        .eq('id', user.id)
        .single();

      if (!groupMember || groupMember.groups?.depth !== 2 || userData?.leadership_level !== 'group') {
        alert('You are not a Group Leader');
        navigate('/');
        return;
      }

      setGroupData(groupMember.groups);

      // Get all subgroups under this group
      const { data: subgroupsData } = await supabase
        .from('groups')
        .select('id, name')
        .eq('parent_id', groupMember.group_id)
        .eq('depth', 3)
        .order('name');

      // Get stats for each subgroup
      const subgroupsWithStats = await Promise.all(
        (subgroupsData || []).map(async (subgroup) => {
          // Get subgroup leader
          const { data: leaderMember } = await supabase
            .from('group_members')
            .select('user_id, users(id, name, avatar_url, leadership_level)')
            .eq('group_id', subgroup.id)
            .maybeSingle();

          let leader = null;
          if (leaderMember?.users?.leadership_level === 'subgroup') {
            leader = leaderMember.users;
          }

          // Get member count
          const { count: memberCount } = await supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', subgroup.id);

          // Get attendance this month
          const { data: members } = await supabase
            .from('group_members')
            .select('user_id')
            .eq('group_id', subgroup.id);

          const memberIds = members?.map(m => m.user_id) || [];
          let attendanceRate = 0;

          if (memberIds.length > 0) {
            const thisMonth = new Date(new Date().setDate(1)).toISOString().split('T')[0];

            const { count: attendanceCount } = await supabase
              .from('attendance_records')
              .select('*', { count: 'exact', head: true })
              .in('user_id', memberIds)
              .gte('marked_at', thisMonth);

            const { count: eventCount } = await supabase
              .from('events')
              .select('*', { count: 'exact', head: true })
              .gte('start_date', thisMonth);

            const totalPossible = (eventCount || 0) * memberIds.length;
            attendanceRate = totalPossible > 0 ? Math.round((attendanceCount / totalPossible) * 100) : 0;
          }

          // Get pending assignment requests
          const { count: pendingRequests } = await supabase
            .from('assignment_requests')
            .select('*', { count: 'exact', head: true })
            .eq('requested_subgroup_id', subgroup.id)
            .eq('status', 'pending');

          return {
            ...subgroup,
            leader: leader,
            memberCount: memberCount || 0,
            attendanceRate,
            pendingRequests: pendingRequests || 0
          };
        })
      );

      setSubgroups(subgroupsWithStats);

    } catch (error) {
      console.error('Error fetching subgroups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubgroupDetails = async (subgroupId) => {
    try {
      setLoading(true);

      // Get subgroup info
      const subgroup = subgroups.find(s => s.id === subgroupId);

      // Get all members
      const { data: membershipData } = await supabase
        .from('group_members')
        .select('id, user_id, joined_at')
        .eq('group_id', subgroupId)
        .order('joined_at', { ascending: false });

      // Get user details for members
      const userIds = membershipData?.map(m => m.user_id) || [];
      let usersData = [];
      
      if (userIds.length > 0) {
        const { data } = await supabase
          .from('users')
          .select('id, name, email, phone, avatar_url, leadership_level')
          .in('id', userIds);
        
        usersData = data || [];
      }

      // Combine membership with user data
      const members = membershipData?.map(member => ({
        ...member,
        users: usersData.find(u => u.id === member.user_id)
      })) || [];

      // Get pending assignment requests
      const { data: requestsData } = await supabase
        .from('assignment_requests')
        .select('id, user_id, requested_at, status')
        .eq('requested_subgroup_id', subgroupId)
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      // Get user details for requests
      const requestUserIds = requestsData?.map(r => r.user_id) || [];
      let requestUsersData = [];
      
      if (requestUserIds.length > 0) {
        const { data } = await supabase
          .from('users')
          .select('id, name, email, avatar_url')
          .in('id', requestUserIds);
        
        requestUsersData = data || [];
      }

      // Combine requests with user data
      const requests = requestsData?.map(request => ({
        ...request,
        users: requestUsersData.find(u => u.id === request.user_id)
      })) || [];

      setSubgroupDetails({
        ...subgroup,
        members,
        pendingRequests: requests
      });

    } catch (error) {
      console.error('Error fetching subgroup details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubgroupClick = (subgroup) => {
    setSelectedSubgroup(subgroup.id);
    fetchSubgroupDetails(subgroup.id);
  };

  const handleBack = () => {
    setSelectedSubgroup(null);
    setSubgroupDetails(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading && !selectedSubgroup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Drill-down view
  if (selectedSubgroup && subgroupDetails) {
    return (
      <>
        <PullToRefresh onRefresh={() => fetchSubgroupDetails(selectedSubgroup)}>
          <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white px-6 pt-6 pb-8 safe-area-top border-b border-gray-200">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 mb-6 hover:opacity-60 transition-opacity"
              >
                <ArrowLeft size={24} weight="light" className="text-gray-900" />
                <span className="font-medium text-gray-900">Back to Subgroups</span>
              </button>

              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{subgroupDetails.name}</h1>
                <p className="text-gray-500 text-sm">
                  {subgroupDetails.memberCount} {subgroupDetails.memberCount === 1 ? 'member' : 'members'}
                </p>
              </div>
            </div>

            <div className="px-4 pt-6 space-y-6">
              {/* Subgroup Leader */}
              {subgroupDetails.leader && (
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <h2 className="text-sm font-medium text-gray-600 mb-3">Subgroup Leader</h2>
                  <div className="flex items-center gap-3">
                    {subgroupDetails.leader.avatar_url ? (
                      <img
                        src={subgroupDetails.leader.avatar_url}
                        alt={subgroupDetails.leader.name}
                        className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center border border-gray-200">
                        <UserCircle size={28} weight="light" className="text-blue-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-gray-900">{subgroupDetails.leader.name}</p>
                      <p className="text-sm text-gray-600">Leader</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-gray-200 text-center">
                  <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <Users size={24} weight="light" className="text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{subgroupDetails.memberCount}</p>
                  <p className="text-xs text-gray-600">Members</p>
                </div>

                <div className="bg-white rounded-2xl p-4 border border-gray-200 text-center">
                  <div className="bg-green-100 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <TrendUp size={24} weight="light" className="text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{subgroupDetails.attendanceRate}%</p>
                  <p className="text-xs text-gray-600">Attendance Rate</p>
                </div>
              </div>

              {/* Pending Assignment Requests */}
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">Pending Requests</h2>
                {subgroupDetails.pendingRequests.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                    <div className="bg-gray-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <ClockCounterClockwise size={32} weight="light" className="text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium mb-1">No pending requests</p>
                    <p className="text-sm text-gray-400">New assignment requests will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {subgroupDetails.pendingRequests.map((request) => (
                      <div key={request.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                        <div className="flex items-center gap-3 mb-3">
                          {request.users?.avatar_url ? (
                            <img
                              src={request.users.avatar_url}
                              alt={request.users.name}
                              className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center border border-gray-200">
                              <UserCircle size={28} weight="light" className="text-blue-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-bold text-gray-900">{request.users?.name}</p>
                            <p className="text-xs text-gray-600">Requested {formatDate(request.requested_at)}</p>
                          </div>
                          <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">
                            Pending
                          </span>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded-xl">
                          <p className="text-sm text-blue-800 text-center">
                            ℹ️ Pending approval by Subgroup Leader
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Members List */}
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-4">
                  Members ({subgroupDetails.members.length})
                </h2>
                {subgroupDetails.members.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                    <UserCircle size={64} className="mx-auto mb-4 text-gray-300" weight="light" />
                    <p className="text-gray-500 font-medium">No members yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {subgroupDetails.members.map((member) => (
                      <div key={member.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                        <div className="flex items-center gap-3">
                          {member.users?.avatar_url ? (
                            <img
                              src={member.users.avatar_url}
                              alt={member.users.name}
                              className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center border border-gray-200">
                              <UserCircle size={28} weight="light" className="text-blue-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-bold text-gray-900">{member.users?.name}</p>
                            <p className="text-xs text-gray-600">{member.users?.email}</p>
                            {member.users?.leadership_level === 'subgroup' && (
                              <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg">
                                Leader
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </PullToRefresh>

        <GroupLeaderBottomNav />
      </>
    );
  }

  // Main subgroups list view
  return (
    <>
      <PullToRefresh onRefresh={fetchSubgroups}>
        <div className="min-h-screen bg-gray-50 pb-20">
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white p-6 pb-8 safe-area-top rounded-b-3xl">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold">My Subgroups</h1>
                <p className="text-blue-100 text-sm mt-1">
                  {subgroups.length} {subgroups.length === 1 ? 'subgroup' : 'subgroups'} under {groupData?.name}
                </p>
              </div>
              <div className="flex gap-1">
                <button className="relative p-2 hover:bg-blue-800 rounded-xl transition-colors">
                  <Bell size={24} weight="light" />
                </button>
                <button 
                  onClick={() => navigate('/group-leader/profile')}
                  className="p-2 hover:bg-blue-800 rounded-xl transition-colors"
                >
                  <UserCircle size={24} weight="light" />
                </button>
              </div>
            </div>
          </div>

          {/* Subgroups List */}
          <div className="px-4 pt-6 space-y-3">
            {subgroups.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <UsersThree size={64} className="mx-auto mb-4 text-gray-300" weight="light" />
                <p className="text-gray-500 font-medium mb-1">No subgroups found</p>
                <p className="text-sm text-gray-400">Subgroups will appear here when created</p>
              </div>
            ) : (
              subgroups.map((subgroup) => (
                <div
                  key={subgroup.id}
                  onClick={() => handleSubgroupClick(subgroup)}
                  className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-gray-900 text-lg">{subgroup.name}</h3>
                    {subgroup.pendingRequests > 0 && (
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">
                        {subgroup.pendingRequests} Pending
                      </span>
                    )}
                  </div>

                  {/* Subgroup Leader */}
                  {subgroup.leader && (
                    <div className="flex items-center gap-2 mb-3">
                      {subgroup.leader.avatar_url ? (
                        <img
                          src={subgroup.leader.avatar_url}
                          alt={subgroup.leader.name}
                          className="w-8 h-8 rounded-lg object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <UserCircle size={20} weight="light" className="text-blue-600" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{subgroup.leader.name}</p>
                        <p className="text-xs text-gray-600">Leader</p>
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Users size={16} weight="light" className="text-gray-600" />
                        <p className="text-xs text-gray-600">Members</p>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{subgroup.memberCount}</p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendUp size={16} weight="light" className="text-gray-600" />
                        <p className="text-xs text-gray-600">Attendance</p>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{subgroup.attendanceRate}%</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </PullToRefresh>

      <GroupLeaderBottomNav />
    </>
  );
}